import { Button, message, Modal, Typography, ModalProps, Flex, Space, Select, Spin, Tabs } from "antd";
import { useEffect, useState } from "react";
import { ApiService } from "api/api.service";

import { TbEdit, TbEye, TbPlus, TbShield, TbX } from "react-icons/tb";

import { toRoleDisplayNames } from "utils/functions";

import { RoleTypes, UserSchemaDto } from "dtos/user.dto";
import { T_MongoId } from "types/_utils.types";
import { DEFAULT_PROFILE_ACL, ProfileACL, ProfileAclCapabilities, ProfileAclTargets } from "dtos/email.dto";
import { IconType } from "react-icons";

interface ManageAclModalProps extends ModalProps {
  profile: T_MongoId | undefined;
  appKey: string | undefined;
}

const capabilityMeta: Record<
  ProfileAclCapabilities,
  { icon: IconType; title: string; description: string; classes: string; dark_classes: string; color: string }
> = {
  [ProfileAclCapabilities.ADMIN]: {
    icon: TbShield,
    title: "Admin",
    description: "Has complete control over the profile. Can update/delete the profile, send messages etc",
    classes: "bg-purple-100 text-purple-500 border-purple-300",
    dark_classes: "text-purple-100 bg-purple-500 border-purple-300",
    color: "purple",
  },
  [ProfileAclCapabilities.EDIT]: {
    icon: TbEdit,
    title: "Edit",
    description: "Can compose and reply mails",
    classes: "bg-blue-100 text-blue-500 border-blue-300",
    dark_classes: "text-blue-100 bg-blue-500 border-blue-300",
    color: "blue",
  },
  [ProfileAclCapabilities.READ]: {
    icon: TbEye,
    title: "Read",
    description: "Only able to view the messages, can not do any operations with it",
    classes: "bg-emerald-100 text-emerald-500 border-emerald-400",
    dark_classes: "text-emerald-100 bg-emerald-500 border-emerald-400",
    color: "green",
  },
};

type FlattenedAclRow<T = keyof ProfileAclTargets> = {
  type: T;
  capability: ProfileAclCapabilities;
  item: T extends "roles" ? RoleTypes : Pick<UserSchemaDto, "_id" | "role" | "name" | "email">;
};

const flattenAcl = (acl: ProfileACL) => {
  const rows: FlattenedAclRow[] = [];
  for (const [cap, { roles, users }] of Object.entries(acl)) {
    for (const role of roles) {
      rows.push({
        type: "roles",
        capability: cap as ProfileAclCapabilities,
        item: role,
      });
    }

    for (const user of users) {
      rows.push({
        type: "users",
        capability: cap as ProfileAclCapabilities,
        item: user,
      });
    }
  }

  return rows;
};

const buildAclObject = (rows: FlattenedAclRow[]) => {
  const acl = { ...DEFAULT_PROFILE_ACL };
  acl.admin.roles = [];

  for (const { type, capability, item } of rows) {
    acl[capability][type].push(item as any);
  }

  return acl;
};

interface AddNewUserProps {
  loading: boolean;
  search: string;
  onSearch: (value: string) => unknown;
  users: Pick<UserSchemaDto, "_id" | "role" | "name" | "email">[];
  acl: FlattenedAclRow<keyof ProfileAclTargets>[];
  setAcl: React.Dispatch<React.SetStateAction<FlattenedAclRow<keyof ProfileAclTargets>[]>>;
}

const AddNewUser = (props: AddNewUserProps) => {
  const [capability, setCapability] = useState(ProfileAclCapabilities.EDIT);
  const [item, setItem] = useState<Pick<UserSchemaDto, "_id" | "role" | "email" | "name"> | undefined>();

  return (
    <Flex gap={6} style={{ maxHeight: 240, overflowY: "auto" }}>
      <Select
        className="w-1/4"
        value={capability}
        suffixIcon={null}
        labelRender={({ value }) => {
          const { title, icon: Icon, classes } = capabilityMeta[value as ProfileAclCapabilities];

          return (
            <Space className={`gap-1 ${classes} !bg-transparent`}>
              <Icon size={14} />
              <Typography.Text className="text-inherit uppercase font-mono">{title}</Typography.Text>
            </Space>
          );
        }}
        onChange={setCapability}
        options={Object.values(ProfileAclCapabilities).map((cap) => ({
          key: cap,
          value: cap,
          label: capabilityMeta[cap].title.toLocaleUpperCase(),
        }))}
      />
      <Select
        key="add-user"
        className="w-full max-w-80"
        placeholder="Add user"
        // prefix={<TbPlus />}
        suffixIcon={null}
        showSearch
        filterOption={false}
        searchValue={props.search}
        onSearch={props.onSearch}
        loading={props.loading}
        notFoundContent={props.loading ? <Spin size="small" /> : null}
        onChange={(userId) => {
          const user = props.users.find((user) => user._id === userId);
          if (user) setItem(user);
        }}
        labelRender={({ value }) => {
          const user = props.users.find((user) => user._id === value);
          if (!user) return value;
          return (
            <Typography.Text>
              {user.name} <span className="font-mono opacity-75">&lt;{user.email}&gt;</span>
            </Typography.Text>
          );
        }}
        options={props.users
          .filter(
            (u) =>
              !props.acl.some(
                (existing) => existing.type === "users" && (existing?.item as UserSchemaDto)._id === u?._id,
              ),
          )
          .map((user) => ({
            label: (
              <Typography.Text>
                {user.name} <span className="font-mono opacity-75">&lt;{user.email}&gt;</span>
              </Typography.Text>
            ),
            value: user._id,
          }))}
      />
      <Button
        disabled={item === undefined}
        onClick={() => {
          if (item === undefined) {
            return message.warning("You must select a role first");
          }

          props.setAcl((prev) => [...prev, { capability, item, type: "users" }]);
        }}
      >
        <TbPlus />
      </Button>
    </Flex>
  );
};

interface AddNewRoleProps {
  acl: FlattenedAclRow<keyof ProfileAclTargets>[];
  setAcl: React.Dispatch<React.SetStateAction<FlattenedAclRow<keyof ProfileAclTargets>[]>>;
}

const AddNewRole = ({ acl, setAcl }: AddNewRoleProps) => {
  const [capability, setCapability] = useState(ProfileAclCapabilities.EDIT);
  const [item, setItem] = useState<RoleTypes | undefined>();

  return (
    <Flex gap={6} style={{ maxHeight: 240, overflowY: "auto" }}>
      <Select
        className="w-1/4"
        value={capability}
        suffixIcon={null}
        labelRender={({ value }) => {
          const { title, icon: Icon, classes } = capabilityMeta[value as ProfileAclCapabilities];

          return (
            <Space className={`gap-1 ${classes} !bg-transparent`}>
              <Icon size={14} />
              <Typography.Text className="text-inherit uppercase font-mono">{title}</Typography.Text>
            </Space>
          );
        }}
        onChange={setCapability}
        options={Object.values(ProfileAclCapabilities).map((cap) => ({
          key: cap,
          value: cap,
          label: capabilityMeta[cap].title.toLocaleUpperCase(),
        }))}
      />
      <Select
        key="add-role"
        className="w-full max-w-80"
        placeholder="Add role"
        // prefix={<TbPlus />}
        suffixIcon={null}
        showSearch
        optionFilterProp="label"
        value={item}
        onChange={setItem}
        options={Object.values(RoleTypes)
          .filter((r) => !acl.some((row) => row.item === r))
          .map((r) => ({ label: toRoleDisplayNames(r), value: r }))}
      />
      <Button
        disabled={item === undefined}
        onClick={() => {
          if (item === undefined) {
            return message.warning("You must select a role first");
          }

          setAcl((prev) => [...prev, { capability, item, type: "roles" }]);
        }}
      >
        <TbPlus />
      </Button>
    </Flex>
  );
};

interface RenderItemsProps {
  target: FlattenedAclRow["type"];
  acl: FlattenedAclRow<keyof ProfileAclTargets>[];
  setAcl: React.Dispatch<React.SetStateAction<FlattenedAclRow<keyof ProfileAclTargets>[]>>;
}

const RenderItems = ({ target, acl, setAcl }: RenderItemsProps) => {
  const [userSearch, setUserSearch] = useState("");
  const usersQuery = ApiService.user.useGetPaginated({ page: 1, limit: 20, search: userSearch, role: [] });
  const userOptions = (usersQuery.data?.result.data ?? []).map((u) => ({
    _id: u._id,
    role: u.role,
    name: u.name,
    email: u.email,
  }));
  const allUsers = Array.from(
    new Map(
      [...acl.filter((row) => row.type === "users").map((row) => row.item as UserSchemaDto), ...userOptions].map(
        (user) => [user._id, user],
      ),
    ).values(),
  );

  const rows = acl.filter((rows) => rows.type === target);

  const renderRole = (row: FlattenedAclRow<"roles">) => {
    return (
      <Flex gap={6} style={{ maxHeight: 240, overflowY: "auto" }}>
        <Select
          className="w-1/4"
          value={row.capability}
          suffixIcon={null}
          labelRender={({ value }) => {
            const { title, icon: Icon, classes } = capabilityMeta[value as ProfileAclCapabilities];

            return (
              <Space className={`gap-1 ${classes} !bg-transparent`}>
                <Icon size={14} />
                <Typography.Text className="text-inherit uppercase font-mono">{title}</Typography.Text>
              </Space>
            );
          }}
          onChange={(capability) => {
            if (row.item === RoleTypes.SUPERADMIN && capability !== ProfileAclCapabilities.ADMIN) {
              return message.warning("Super Admin can not be demoted");
            }

            setAcl((prev) =>
              prev.map((item) => {
                if (item.item !== row.item) return item;

                return {
                  ...item,
                  capability,
                };
              }),
            );
          }}
          options={Object.values(ProfileAclCapabilities).map((cap) => ({
            key: cap,
            value: cap,
            label: capabilityMeta[cap].title.toLocaleUpperCase(),
          }))}
        />
        <Select
          key="show-role"
          className="w-full max-w-80"
          placeholder="Add role"
          // prefix={<TbPlus />}
          suffixIcon={null}
          showSearch
          optionFilterProp="label"
          value={row.item}
          // disabled={row.item === RoleTypes.SUPERADMIN}
          onChange={(role) => {
            if (row.item === RoleTypes.SUPERADMIN) {
              return message.warning("Can not change super admin role");
            }

            setAcl((prev) =>
              prev.map((item) => {
                if (item.type !== "roles" || item.item !== row.item) return item;
                return {
                  ...item,
                  item: role,
                };
              }),
            );
          }}
          labelRender={({ value }) => toRoleDisplayNames(value as unknown as RoleTypes)}
          options={Object.values(RoleTypes)
            .filter((r) => !acl.some((row) => row.type === "roles" && row.item === r))
            .map((r) => ({ label: toRoleDisplayNames(r), value: r }))}
        />
        <Button
          danger
          disabled={row.item === RoleTypes.SUPERADMIN}
          onClick={() => setAcl((prev) => prev.filter(({ item }) => row.item !== item))}
        >
          <TbX />
        </Button>
      </Flex>
    );
  };

  const renderUser = (row: FlattenedAclRow<"users">) => {
    return (
      <Flex gap={6} style={{ maxHeight: 240, overflowY: "auto" }}>
        <Select
          className="w-1/4"
          value={row.capability}
          suffixIcon={null}
          labelRender={({ value }) => {
            const { title, icon: Icon, classes } = capabilityMeta[value as ProfileAclCapabilities];

            return (
              <Space className={`gap-1 ${classes} !bg-transparent`}>
                <Icon size={14} />
                <Typography.Text className="text-inherit uppercase font-mono">{title}</Typography.Text>
              </Space>
            );
          }}
          onChange={(capability) => {
            setAcl((prev) =>
              prev.map((item) => {
                if (item.item !== row.item) return item;

                return {
                  ...item,
                  capability,
                };
              }),
            );
          }}
          options={Object.values(ProfileAclCapabilities).map((cap) => ({
            key: cap,
            value: cap,
            label: capabilityMeta[cap].title.toLocaleUpperCase(),
          }))}
        />
        <Select
          key="show-user"
          className="w-full max-w-80"
          placeholder="Add user"
          // prefix={<TbPlus />}
          suffixIcon={null}
          showSearch
          filterOption={false}
          // searchValue={userSearch}
          onSearch={setUserSearch}
          loading={usersQuery.isFetching}
          notFoundContent={usersQuery.isFetching ? <Spin size="small" /> : null}
          value={row.item._id}
          onChange={(userId) => {
            setAcl((prev) => {
              return prev.map((item) => {
                if (item.type !== "users" || (item.item as UserSchemaDto)._id !== row.item._id) return item;

                return {
                  ...item,
                  item: allUsers.find((user) => user._id === userId)!,
                };
              });
            });
          }}
          labelRender={({ value }) => {
            const user = allUsers.find((user) => user._id === value);
            if (!user) return value;
            return (
              <Typography.Text>
                {user.name} <span className="font-mono opacity-75">&lt;{user.email}&gt;</span>
              </Typography.Text>
            );
          }}
          options={allUsers
            .filter((u) => !acl.some((row) => row.type === "users" && (row.item as UserSchemaDto)._id === u._id))
            .map((user) => ({
              label: (
                <Typography.Text>
                  {user.name} <span className="font-mono opacity-75">&lt;{user.email}&gt;</span>
                </Typography.Text>
              ),
              value: user._id,
            }))}
        />
        <Button
          danger
          onClick={() =>
            setAcl((prev) =>
              prev.filter(({ item, type }) => type === "roles" || row.item._id !== (item as UserSchemaDto)._id),
            )
          }
        >
          <TbX />
        </Button>
      </Flex>
    );
  };

  return (
    <Space direction="vertical" className="w-full">
      <div className="max-h-64 hide-scrollbar overflow-y-auto">
        <Flex vertical gap={8}>
          {rows.map((row) =>
            row.type === "roles"
              ? renderRole(row as FlattenedAclRow<"roles">)
              : renderUser(row as FlattenedAclRow<"users">),
          )}
        </Flex>
      </div>
      {target === "roles" ? (
        <AddNewRole acl={acl} setAcl={setAcl} />
      ) : (
        <AddNewUser
          acl={acl}
          setAcl={setAcl}
          loading={usersQuery.isFetching}
          search={userSearch}
          onSearch={setUserSearch}
          users={userOptions}
        />
      )}
    </Space>
  );
};

export const ManageAclModal = ({ profile, appKey, ...props }: ManageAclModalProps) => {
  const updateAcl = ApiService.email.useUpdateProfileACL();

  const profilesQuery = ApiService.email.useGetAttachedProfiles({ appKey });
  const savedAcl = profilesQuery.data?.result.find((pf) => pf._id === profile)?.acl;
  const [target, setTarget] = useState<keyof ProfileAclTargets>("roles");
  const [acl, setAcl] = useState<FlattenedAclRow[]>(() => flattenAcl(DEFAULT_PROFILE_ACL));

  useEffect(() => {
    if (savedAcl) setAcl(flattenAcl(savedAcl));
  }, [savedAcl]);

  const onCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAcl(flattenAcl(savedAcl || DEFAULT_PROFILE_ACL));
    props.onCancel?.(e);
  };

  const onOk = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (!profile) {
      return message.warning("Profile ID not found");
    }

    updateAcl.mutate(
      { id: profile, acl: buildAclObject(acl) },
      {
        onSettled() {
          profilesQuery.refetch();
          onCancel(e);
        },
        onSuccess(data) {
          message.success(data.message || "ACL updated successfully");
        },
      },
    );
  };

  return (
    <Modal {...props} destroyOnHidden onCancel={onCancel} onOk={onOk}>
      <Tabs
        activeKey={target}
        onChange={(key) => setTarget(key as keyof ProfileAclTargets)}
        items={[
          {
            key: "roles",
            label: "Roles",
            children: <RenderItems target="roles" acl={acl} setAcl={setAcl} />,
          },
          {
            key: "users",
            label: "Users",
            children: <RenderItems target="users" acl={acl} setAcl={setAcl} />,
          },
        ]}
      />
    </Modal>
  );
};
